/**
 * Drives the entrance state machine from the authentication state.
 *
 * All the timing lives here; the reducer stays a pure transition table. The
 * one thing this hook must never do is decide whether someone is allowed in —
 * it reads `authStatus` and reacts to it.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  entranceReducer,
  ENTRANCE_TIMELINE,
  initialStateFor,
  isOpened,
  type EntranceEvent,
  type EntranceState,
} from './machine';
import { EntranceAudio, readSoundPreference, writeSoundPreference } from './sound';

export type AuthStatus = 'checking' | 'signed-in' | 'signed-out';

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let query: MediaQueryList;
    try {
      query = window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch {
      return;
    }
    const listener = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener?.('change', listener);
    return () => query.removeEventListener?.('change', listener);
  }, []);

  return reduced;
}

export interface Entrance {
  state: EntranceState;
  reducedMotion: boolean;
  muted: boolean;
  toggleMuted(): void;
  /** Called by the sign-in form the moment it submits. */
  authStarted(): void;
  /** Called by the sign-in form when the server refuses. */
  authFailed(): void;
}

export function useEntrance(authStatus: AuthStatus): Entrance {
  const reducedMotion = usePrefersReducedMotion();
  const options = useMemo(() => ({ reducedMotion }), [reducedMotion]);

  // The initial state is decided once, from whatever the session turned out to
  // be. A page that loads already signed in was never locked.
  const [state, send] = useReducer(
    (current: EntranceState, event: EntranceEvent) => entranceReducer(current, event, options),
    authStatus,
    (status) => initialStateFor(status, { reducedMotion }),
  );

  // Someone who has asked for less motion has usually asked for less noise
  // too, so that preference is honoured without a second setting.
  const [muted, setMuted] = useState(() => reducedMotion || readSoundPreference() === 'off');
  const audio = useRef<EntranceAudio | null>(null);
  if (audio.current === null) audio.current = new EntranceAudio(muted);

  useEffect(() => {
    audio.current?.setMuted(muted);
  }, [muted]);

  useEffect(() => {
    const instance = audio.current;
    return () => instance?.dispose();
  }, []);

  const toggleMuted = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      writeSoundPreference(next ? 'off' : 'on');
      return next;
    });
  }, []);

  const authStarted = useCallback(() => send({ type: 'AUTH_STARTED' }), []);
  const authFailed = useCallback(() => send({ type: 'AUTH_FAILED' }), []);

  // Authentication is authoritative. Everything below reacts to it.
  const previousStatus = useRef<AuthStatus>(authStatus);
  useEffect(() => {
    const previous = previousStatus.current;
    previousStatus.current = authStatus;
    if (previous === authStatus) return;

    if (authStatus === 'signed-out') {
      send({ type: 'SIGNED_OUT' });
      return;
    }
    if (authStatus !== 'signed-in') return;

    // Restoring a session on load is not an arrival. Only a sign-in that
    // happened on this page, from a locked door, opens the doors.
    if (previous === 'checking') {
      send({ type: 'SESSION_RESTORED' });
      return;
    }
    send({ type: 'AUTH_SUCCEEDED' });
  }, [authStatus]);

  // The sequence. Every timer is cleared on unmount and on a state change, so
  // an interrupted entrance leaves nothing running.
  useEffect(() => {
    if (state !== 'ACCESS_GRANTED') return;
    const sound = audio.current;
    sound?.authAccepted();

    if (reducedMotion) {
      // A short fade instead of the journey. The dashboard has already mounted
      // behind the doors by this point, exactly as it otherwise would.
      const timer = window.setTimeout(
        () => send({ type: 'SKIP' }),
        ENTRANCE_TIMELINE.reducedMotionMs,
      );
      return () => window.clearTimeout(timer);
    }

    const unlock = window.setTimeout(() => {
      sound?.unlock();
      send({ type: 'UNLOCK_RELEASED' });
    }, ENTRANCE_TIMELINE.unlockAtMs);
    return () => window.clearTimeout(unlock);
  }, [state, reducedMotion]);

  useEffect(() => {
    if (state !== 'UNLOCKING') return;
    const start = window.setTimeout(() => {
      audio.current?.movementStarted(ENTRANCE_TIMELINE.travelMs);
      send({ type: 'MOVEMENT_STARTED' });
    }, ENTRANCE_TIMELINE.movementAtMs - ENTRANCE_TIMELINE.unlockAtMs);
    return () => window.clearTimeout(start);
  }, [state]);

  useEffect(() => {
    if (state !== 'OPENING') return;
    const settle = window.setTimeout(() => {
      audio.current?.docked();
      // The room, once you are in it. It fades out rather than running on.
      audio.current?.ambience();
      send({ type: 'MOVEMENT_SETTLED' });
    }, ENTRANCE_TIMELINE.dockAtMs - ENTRANCE_TIMELINE.movementAtMs);
    return () => window.clearTimeout(settle);
  }, [state]);

  // The watchdog. If any of the above never fires — a backgrounded tab, a
  // throttled timer, an animation the browser refused — the doors open anyway.
  // Access must never depend on a decoration behaving.
  useEffect(() => {
    if (isOpened(state) || state === 'LOCKED' || state === 'AUTHENTICATING') return;
    const watchdog = window.setTimeout(() => {
      audio.current?.stopMovement();
      send({ type: 'SKIP' });
    }, ENTRANCE_TIMELINE.watchdogMs);
    return () => window.clearTimeout(watchdog);
  }, [state]);

  return { state, reducedMotion, muted, toggleMuted, authStarted, authFailed };
}
